import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Tag,
  Button as AntButton,
  Space,
  Breadcrumb,
  Col,
  Row,
  Typography,
  Select,
} from 'antd';
import styled from 'styled-components';
import {
  EnvironmentOutlined,
  PhoneOutlined,
  MailOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import {
  FacilityCreateType,
  FacilityDetailType,
  FacilityDetailWithDayjsTimes,
  FacilitySearchParams,
  FacilityType,
} from '../types/types';
import {
  createFacilityRequest,
  deleteFacilityRequest,
  updateFacilityRequest,
  searchFacilitiesRequest,
  getFacilityByIdRequest,
} from '../api/requests/facility';
import { COLORS, TEXT_SIZES } from '../utils/constants';
import dayjs from 'dayjs';
import { debounce } from '../utils/utils';
import { CustomTable } from '../components/common/CustomTable';
import FacilityModal, {
  UnifiedImage,
} from '../components/features/Facility/FacilityFormModal';
import DeleteFacilityModal from '../components/features/Facility/DeleteFacilityModal';
import Button from '../components/common/Button';
import { useSnackbar } from '../contexts/SnackbarContext/SnackbarProvider';
import {
  bulkUploadFilesRequest,
  deleteAttachmentRequest,
  updatePrimaryAttachmentRequest,
} from '../api/requests/attachments';
import { EntityType, FacilityStatus } from '../enums/enums';
import { Outlet, useLocation, useNavigate, useOutlet } from 'react-router-dom';
import { FACILITYMANAGEMENTPATH } from '../routes/routePaths';
import { StyledSearch, StyledSelect } from './UserManagement';

const { Option } = Select;

export const getFileOrders = (
  imageFiles: UnifiedImage[],
  allImageFiles?: UnifiedImage[],
) => {
  return imageFiles.map((imageFile: UnifiedImage, index: number) => ({
    fileName: imageFile.file?.name ?? '',
    displayOrder:
      allImageFiles && allImageFiles.length > 0
        ? allImageFiles.findIndex(
            (img) => img.file?.name === imageFile.file?.name,
          ) + 1
        : index + 1,
    isPrimary: imageFile.isPrimary,
  }));
};

const Facility = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const outlet = useOutlet();
  const snackbar = useSnackbar();
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [modalData, setModalData] =
    useState<FacilityDetailWithDayjsTimes | null>(null);
  const [facilities, setFacilities] = useState<FacilityType[]>([]);
  const [isFacilitesDataProcessing, setIsFacilitesDataProcessing] =
    useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [facilityToDelete, setFacilityToDelete] = useState<FacilityType | null>(
    null,
  );
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [status, setStatus] = useState<number | undefined>();
  const [totalCount, setTotalCount] = useState<number>(0);

  const pageSize = 8;

  const columns = [
    {
      title: 'Facility Name',
      dataIndex: 'facilityName',
      key: 'facility-name',
      width: 200,
      render: (
        facilityName: FacilityType['facilityName'],
        record: FacilityType,
      ) => {
        return (
          <ColumnSpaceWrapper direction="vertical">
            <Typography.Link onClick={() => handleRowClick(record)}>
              {facilityName}
            </Typography.Link>
            <FlexRow>
              <IconWrapper>
                <EnvironmentOutlined />
              </IconWrapper>
              <StyledText>
                {record.facilityAddress}, {record.city}, {record.state},{' '}
                {record.country}, {record.zipCode}
              </StyledText>
            </FlexRow>
          </ColumnSpaceWrapper>
        );
      },
    },
    {
      title: 'Contact Person',
      dataIndex: 'facilityContactName',
      key: 'contact-person',
      width: 200,
      render: (
        facilityContactName: FacilityType['facilityContactName'],
        record: FacilityType,
      ) => {
        return (
          <ColumnSpaceWrapper direction="vertical">
            <Typography.Text>{facilityContactName}</Typography.Text>
            <FlexRow>
              <IconWrapper>
                <PhoneOutlined
                  style={{
                    transform: 'rotate(90deg)',
                  }}
                />
              </IconWrapper>
              <StyledText>{record.facilityContactPhone}</StyledText>
            </FlexRow>
            <FlexRow>
              <IconWrapper>
                <MailOutlined />
              </IconWrapper>
              <StyledText>{record.facilityContactEmail}</StyledText>
            </FlexRow>
          </ColumnSpaceWrapper>
        );
      },
    },
    {
      title: 'Sports',
      dataIndex: 'sportsNames',
      key: 'sports',
      width: 150,
      render: (sports: string[]) => {
        const limit = 2;
        const sportsToShow =
          sports.length > limit
            ? sports.slice(0, limit).join(', ') + ',...'
            : sports.join(', ');
        return <>{sportsToShow}</>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'facilityStatusName',
      key: 'status',
      width: 100,
      render: (status: FacilityType['facilityStatusName']) => {
        const colorMap = {
          Approved: 'cyan',
          Closed: 'red',
          Pending: 'orange',
        };
        return (
          <CustomTag color={colorMap[status as keyof typeof colorMap]}>
            {status}
          </CustomTag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: FacilityType) => (
        <StyledSpaceWrapper>
          <StyledActionButton
            type="text"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(record);
            }}
          >
            Edit
          </StyledActionButton>
          <StyledActionButton
            type="text"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(record);
            }}
          >
            Delete
          </StyledActionButton>
        </StyledSpaceWrapper>
      ),
    },
  ];

  const fetchFacilities = useCallback(
    async (page = 1, searchTerm = '', status?: number) => {
      try {
        setIsFacilitesDataProcessing(true);
        const params: FacilitySearchParams = {
          PageNumber: Math.max(0, page - 1),
          PageSize: pageSize,
          SearchQuery: searchTerm || undefined,
          FacilityStatus: status,
        };
        const result = await searchFacilitiesRequest(params);
        setTotalCount(result?.value?.totalCount || 0);
        setFacilities(result?.value?.rows || []);
        setCurrentPage(page);
      } catch (error) {
        snackbar.error('Failed to load facilities');
        setFacilities([]);
      } finally {
        setIsFacilitesDataProcessing(false);
      }
    },
    [snackbar, pageSize],
  );

  const handleEdit = async (facility: FacilityType) => {
    try {
      const response = await getFacilityByIdRequest(facility.facilityId, true);
      if (response && response?.value) {
        const parsedValues: FacilityDetailWithDayjsTimes = {
          ...response.value,
          openingTime: dayjs(response.value.openingTime, 'HH:mm:ss'),
          closingTime: dayjs(response.value.closingTime, 'HH:mm:ss'),
        };
        setModalData(parsedValues);
        setModalVisible(true);
      }
    } catch (error) {}
  };

  const handleDelete = (facility: FacilityType) => {
    setFacilityToDelete(facility);
    setDeleteModalVisible(true);
  };

  const handleConfirmDelete = async () => {
    try {
      if (facilityToDelete) {
        const response = await deleteFacilityRequest(
          facilityToDelete.facilityId,
        );
        if (response?.isSuccess) {
          setDeleteModalVisible(false);
          setFacilityToDelete(null);
          snackbar.success('Facility deleted successfully');
          fetchFacilities(currentPage, searchTerm, status);
        } else {
          snackbar.error(response?.error);
        }
      }
    } catch (error: any) {
      snackbar.error(error?.Error || 'Error deleting facility');
    }
  };

  const handleAddNew = () => {
    setModalData(null);
    setModalVisible(true);
  };

  const formatDetailToCreateType = (
    values: FacilityDetailType,
  ): FacilityCreateType => {
    return {
      facilityName: values.facilityName,
      facilityDescription: values.facilityDescription,
      facilityLocation: values.facilityLocation,
      facilityAddress: values.facilityAddress,
      state: values.state,
      country: values.country,
      city: values.city,
      zipCode: values.zipCode,
      facilityLatitude: values.facilityLatitude,
      facilityLongitude: values.facilityLongitude,
      facilityContactName: values.facilityContactName,
      facilityContactPhone: values.facilityContactPhone,
      facilityContactEmail: values.facilityContactEmail,
      facilityOwnerId: values.facilityOwnerId,
      openingTime: values.openingTime,
      closingTime: values.closingTime,
      sportsIds: values.sports?.map((sport) => sport.sportId) || [],
      facilityAmenityIds: values.facilityAmenities || [],
      facilityStatus: values.facilityStatus,
    };
  };

  const handleModalSubmit = async (
    values: FacilityDetailType,
    imageFiles: UnifiedImage[],
    deletedAttachmentIds: number[],
  ) => {
    try {
      setIsFacilitesDataProcessing(true);
      const data = formatDetailToCreateType(values);
      if (modalData) {
        // Update existing facility
        const response = await updateFacilityRequest({
          ...data,
          facilityId: Number(modalData.facilityId),
        });
        if (response?.isSuccess) {
          try {
            if (deletedAttachmentIds.length > 0) {
              await Promise.all(
                deletedAttachmentIds.map((attachmentId) =>
                  deleteAttachmentRequest(attachmentId),
                ),
              );
            }
            if (
              imageFiles &&
              imageFiles.length > 0 &&
              imageFiles.some((img) => img.isNew)
            ) {
              const newImages = imageFiles.filter((img) => img.isNew);
              const uploadResponse = await bulkUploadFilesRequest(
                newImages.map((img) => img.file!),
                EntityType.Facility,
                Number(modalData.facilityId),
                'Facility Image',
                getFileOrders(newImages, imageFiles),
              );
              if (uploadResponse) {
                if (
                  uploadResponse.value?.failureCount &&
                  uploadResponse.value?.failureCount > 0
                ) {
                  snackbar.error(
                    `${uploadResponse.value.failureCount} image(s) failed to upload.`,
                  );
                }
              }
            }
            const primaryImage = imageFiles.find(
              (img) => img.isPrimary && !img.isNew,
            );
            const existingPrimaryImage = modalData.attachments.find(
              (attachment) => attachment.isPrimary === true,
            );
            if (
              imageFiles &&
              imageFiles.length > 0 &&
              existingPrimaryImage?.attachmentId !== primaryImage?.id &&
              primaryImage
            ) {
              await updatePrimaryAttachmentRequest(
                Number(modalData.facilityId),
                primaryImage.id!,
              );
            }
          } catch (error) {
            console.error('Error uploading image:', error);
          }
          setModalVisible(false);
          await fetchFacilities(currentPage, searchTerm, status);
          snackbar.success('Facility updated successfully');
        } else {
          snackbar.error(response?.error);
        }
      } else {
        // Create new facility
        const response = await createFacilityRequest(data);
        if (response?.isSuccess && response?.value) {
          try {
            if (imageFiles && imageFiles.length > 0) {
              const uploadResponse = await bulkUploadFilesRequest(
                imageFiles.map((img) => img.file!),
                EntityType.Facility,
                response.value,
                'Facility Image',
                getFileOrders(imageFiles),
              );
              if (uploadResponse) {
                if (
                  uploadResponse.value?.failureCount &&
                  uploadResponse.value?.failureCount > 0
                ) {
                  snackbar.error(
                    `${uploadResponse.value.failureCount} image(s) failed to upload.`,
                  );
                }
              }
            }
          } catch (error) {
            console.error('Error uploading image:', error);
          }
          setModalVisible(false);
          await fetchFacilities(1, searchTerm, status);
          snackbar.success('Facility created successfully');
        } else {
          snackbar.error(response?.error);
        }
      }
    } catch (error: any) {
      snackbar.error(error?.Error || 'Error occured, please try again');
    } finally {
      setIsFacilitesDataProcessing(false);
    }
  };

  const handleRowClick = (record: FacilityType) => {
    navigate(`${FACILITYMANAGEMENTPATH}/${record.facilityId}`);
  };

  // debounced search function
  const debouncedFetchFacilities = useMemo(
    () =>
      debounce((page, query, status) => {
        fetchFacilities(page, query, status);
      }, 500),
    [fetchFacilities],
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedFetchFacilities(1, value, status);
  };

  const onStatusChange = (value: unknown) => {
    const statusValue = value as number | undefined;
    setStatus(statusValue);
    fetchFacilities(1, searchTerm, statusValue);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchFacilities(page, searchTerm, status);
  };

  useEffect(() => {
    location.state?.from
      ? fetchFacilities(currentPage, searchTerm, status)
      : fetchFacilities();
  }, [location.state?.from, fetchFacilities]);

  return (
    <Container>
      {!outlet && (
        <Header>
          <StyledSpace direction="horizontal" size={24}>
            <StyledSearch
              placeholder="Search by Facility Name, Sport..."
              onChange={handleSearchChange}
              enterButton
              size="large"
              allowClear
              value={searchTerm}
              disabled={isFacilitesDataProcessing}
            />
            <StyledSelect
              value={status}
              onChange={onStatusChange}
              placeholder="Select Status"
              allowClear
              disabled={isFacilitesDataProcessing}
            >
              {Object.entries(FacilityStatus)
                .filter(([key, value]) => isNaN(Number(key)))
                .map(([key, value]) => (
                  <Option key={value} value={value}>
                    {key}
                  </Option>
                ))}
            </StyledSelect>
            <Button
              type="primary"
              icon={<PlusCircleOutlined style={{ fontSize: TEXT_SIZES.LG }} />}
              onClick={handleAddNew}
              width="182px"
              disabled={isFacilitesDataProcessing}
            >
              Add New Facility
            </Button>
          </StyledSpace>
        </Header>
      )}

      {outlet ? (
        <Outlet />
      ) : (
        <CustomTable
          columns={columns}
          dataSource={facilities}
          rowKey="facilityId"
          loading={isFacilitesDataProcessing}
          pagination={{
            pageSize: pageSize,
            current: currentPage,
            total: totalCount,
            onChange: (page) => handlePageChange(page),
          }}
        />
      )}

      {/* 
        Facility Modal for adding/editing facilites
      */}
      <FacilityModal
        visible={modalVisible}
        onClose={() => {
          setModalVisible(false);
          setModalData(null);
        }}
        onSubmit={handleModalSubmit}
        initialValues={modalData}
      />

      <DeleteFacilityModal
        visible={deleteModalVisible}
        facilityName={facilityToDelete?.facilityName}
        onCancel={() => {
          setDeleteModalVisible(false);
          setFacilityToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
      />
    </Container>
  );
};

const Container = styled(Col)`
  padding: 8px;
`;

const Header = styled(Row)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const StyledSpace = styled(Space)`
  width: 100%;
  & .ant-space-item:first-child {
    flex: 1;
  }
`;

export const StyledBreadcrumbItem = styled(Breadcrumb.Item)`
  &.ant-breadcrumb-link:hover {
    cursor: pointer;
    color: ${COLORS.skyBlue.primary};
  }
`;

export const StyledSpaceWrapper = styled(Space)`
  &.ant-space {
    gap: 0px !important;
  }
`;
export const StyledActionButton = styled(AntButton)`
  color: ${COLORS.skyBlue.primary};
`;

const ColumnSpaceWrapper = styled(Space)`
  gap: 1px;
`;
const FlexRow = styled(Row)`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const StyledText = styled(Typography.Text)`
  color: ${COLORS.grey.primary};
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  white-space: normal;
  flex: 1;
  min-width: 0;
`;

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${COLORS.grey.secondaryLight};
  padding: 4px;
  border-radius: 50px;
`;

export const CustomTag = styled(Tag)`
  &.ant-tag {
    border-radius: 50px;
    padding: 4px;
    padding-right: 15px;
    padding-left: 15px;
  }
`;

export default Facility;
